import { Component, ViewChild } from '@angular/core';
import { Spettacolo, UtenteAccredito } from '../../../../shared/interface';
import { Table } from 'primeng/table';
import { ApiService } from '../../../../services/api.service';
import { ConfirmationService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmPopup } from 'primeng/confirmpopup';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-utenti-accredito-spettacolo-backoffice',
  templateUrl: './utenti-accredito-spettacolo-backoffice.component.html',
  styleUrl: './utenti-accredito-spettacolo-backoffice.component.css'
})
export class UtentiAccreditoSpettacoloBackofficeComponent {

  constructor(
    private apiService: ApiService,
    private confirmationService: ConfirmationService,    
    private router: Router,
    private route: ActivatedRoute,    
  ) {}
  
  @ViewChild('delete') delete!: ConfirmPopup;
  
  utenti: UtenteAccredito[] = [];
  utentiAll: UtenteAccredito[] = [];
  utente?: UtenteAccredito;
  spettacolo?: Spettacolo;
  selectedUtenteId: number | null = null;

  loading: boolean = false;
  add: boolean = false;
  edit: boolean = false;

  formUtente!: FormGroup;  
  
  ngOnInit(): void {
    this.loading = true;
  
    this.formUtente = new FormGroup({
      username: new FormControl(null),
      email: new FormControl(null, [Validators.required, Validators.email]),
      password: new FormControl(null, [Validators.required,Validators.minLength(4)]),
      confirm_password: new FormControl(null, [Validators.required,Validators.minLength(4)]),
    }, { validators: this.passwordMatchValidator });

    // Recupero l'id dall'url
    this.route.params.subscribe(params => {
      const id = params['id'];

      this.apiService.getSpettacolo(id).subscribe({
        next: (data) => this.spettacolo = data,
        error: (err) => this.router.navigate(['/backoffice/spettacoli']),
        complete: () => {

          this.apiService.setUtentiAccreditoSpettacolo(id);
          this.apiService.utentiAccreditiSpettacoloChanged.subscribe({
            next: (data) => {
              this.utenti = [...data];
              this.loading = false;
            },
            error: (err) => this.apiService.message('error', 'Errore', err.error.message.message),
          });

        }
      })      
    })    
  }
  
  onGlobalFilter(table: Table, event: Event) {
    table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }

  get utentiSelezionabili(): { label: string; value: number }[] {
    const onShow = new Set((this.utenti || []).map((u) => u.id));
    return (this.utentiAll || [])
      .filter((u) => !onShow.has(u.id))
      .map((u) => ({
        value: u.id,
        label: [u.email, u.username].filter(Boolean).join(' · ') || `Utente #${u.id}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'it'));
  }

  openAdd() {
    this.selectedUtenteId = null;
    this.setPasswordRequired(true);
    this.add = true;
    this.apiService.getUtentiAccrediti().subscribe({
      next: (data) => (this.utentiAll = data || []),
      error: (err) => this.apiService.message('error', 'Errore', err?.error?.message?.message || 'Impossibile caricare gli utenti'),
    });
  }

  onSelectUtenteEsistente(id: number | null) {
    this.selectedUtenteId = id;
    if (!id) {
      this.formUtente.patchValue({ username: null, email: null, password: null, confirm_password: null });
      this.setPasswordRequired(true);
      return;
    }
    const u = this.utentiAll.find((x) => x.id === id);
    if (!u) return;
    this.formUtente.patchValue({
      username: u.username || null,
      email: u.email || null,
      password: null,
      confirm_password: null,
    });
    this.setPasswordRequired(false);
  }

  private setPasswordRequired(required: boolean) {
    const password = this.formUtente.get('password');
    const confirm = this.formUtente.get('confirm_password');
    if (required) {
      password?.setValidators([Validators.required, Validators.minLength(4)]);
      confirm?.setValidators([Validators.required, Validators.minLength(4)]);
    } else {
      password?.clearValidators();
      confirm?.clearValidators();
    }
    password?.updateValueAndValidity({ emitEvent: false });
    confirm?.updateValueAndValidity({ emitEvent: false });
  }

  onOpenDelete(event: Event, utente: UtenteAccredito) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Sei sicuro di voler eliminare l'utente?`,
      reject: () => this.confirmationService.close(),
      accept: () => {
        this.apiService.deleteUtenteAccreditoSpettacolo(utente.id, this.spettacolo!.id).subscribe({
          next: () => this.apiService.setUtentiAccreditoSpettacolo(this.spettacolo!.id),
          error: (err) => this.apiService.message('error','Errore',err.error.message.message),
          complete: () => {          
            this.confirmationService.close();            
            this.apiService.message('success','Eliminazione',"L'utente è stato eliminato con successo");
          },
        });
      },
    });
  }
  
  onDelete() {
    this.delete.accept();
  }
  
  onClose() {
    this.delete.reject();
  }

  passwordMatchValidator(control: AbstractControl) {
    const password = control.get('password')?.value;
    const confirm = control.get('confirm_password')?.value;
    if (!password && !confirm) return null;
    return password === confirm ? null : { mismatch: true };
  }
  
  onOpenEdit(utente: UtenteAccredito) {
    this.utente = utente;
    this.selectedUtenteId = null;
    this.setPasswordRequired(false);
    this.formUtente.patchValue({ username: this.utente?.username, email: this.utente?.email, password: null, confirm_password: null });
    this.edit = true;
  }

  onCloseModal() {    
    this.add = false;
    this.edit = false;
    this.utente = undefined;
    this.selectedUtenteId = null;
    this.setPasswordRequired(true);
    this.formUtente.reset();
    this.formUtente.markAsPristine();
    this.formUtente.markAsUntouched();
  }

  onAdd() {
    let utente = {
      username: this.formUtente.get('username')?.value || '',
      email: this.formUtente.get('email')?.value || '',
      password: this.selectedUtenteId ? '' : (this.formUtente.get('password')?.value || ''),
      id_spettacolo: this.spettacolo?.id || 0,
    };

    this.apiService.addUtenteAccredito(utente).subscribe({
      next: (data) => this.apiService.setUtentiAccreditoSpettacolo(this.spettacolo!.id),
      error: (err) => this.apiService.message('error', 'Errore', err.error.message.message),
      complete: () => {
        this.onCloseModal(),
        this.apiService.message('success','Creazione','Creazione utente effettuata con successo!');
      }
    });
  }

  onEdit() {
    let utente = {
      username: this.formUtente.get('username')?.value,
      email: this.formUtente.get('email')?.value || '',
      spettacolo: this.spettacolo?.id || 0,
    };

    let password = {
      password: this.formUtente.get('password')?.value || '',
    };

    this.apiService.updateUtenteAccredito(utente, this.utente!.id).subscribe({
      next: (data) => this.apiService.setUtentiAccreditoSpettacolo(this.spettacolo!.id),
      error: (err) => this.apiService.message('error', 'Errore', err.error.message.message),
      complete: () => {
        if (password.password.length > 0) {
          this.apiService.updatePasswordUtenteAccredito(password, this.utente!.id).subscribe({
            next: (data) => this.apiService.setUtentiAccreditoSpettacolo(this.spettacolo!.id),
            error: (err) => this.apiService.message('error','Errore',err.error.message.message,),
            complete: () => {
              this.onCloseModal();
              this.apiService.message('success','Modifica','Modifica utente effettuata con successo!');
            },
          });
        } else {
          this.onCloseModal();
          this.apiService.message('success','Modifica','Modifica utente effettuata con successo!');
        }
      },
    });
  }
}
